// Safety — Safety Walks API
// Thin service layer over the shared `api` util.

import api from './api';
import type {
    SafetyWalk,
    SafetyObservation,
    SafetyWalkSettings,
    WalkListResponse,
    WalkDetailResponse,
    ObservationListResponse,
    MyTasksResponse,
    WalkStats,
} from '../types/safety-walk.types';

const WALKS_BASE = '/api/safety/walks';
const OBS_BASE = '/api/safety/observations';

export interface WalkListParams {
    status?: string;
    search?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    page?: number;
    limit?: number;
}

export interface ObservationListParams {
    walkId?: number;
    status?: string;
    riskRating?: string;
    overdue?: boolean;
    search?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    page?: number;
    limit?: number;
}

/* ------------------------------------------------------------------ */
/*  Walks                                                              */
/* ------------------------------------------------------------------ */

export async function listWalks(params: WalkListParams = {}): Promise<WalkListResponse> {
    const qs = new URLSearchParams();
    if (params.status && params.status !== 'All') qs.append('status', params.status);
    if (params.search) qs.append('search', params.search);
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortDir) qs.append('sortDir', params.sortDir);
    if (params.page) qs.append('page', String(params.page));
    if (params.limit) qs.append('limit', String(params.limit));
    return api.get<WalkListResponse>(`${WALKS_BASE}${qs.toString() ? `?${qs}` : ''}`);
}

export async function getWalk(id: number): Promise<WalkDetailResponse> {
    return api.get<WalkDetailResponse>(`${WALKS_BASE}/${id}`);
}

export async function createWalk(payload: Partial<SafetyWalk>): Promise<{ success: boolean; walk: SafetyWalk }> {
    return api.post(`${WALKS_BASE}`, payload);
}

export async function updateWalk(id: number, payload: Partial<SafetyWalk>): Promise<{ success: boolean; walk: SafetyWalk }> {
    return api.patch(`${WALKS_BASE}/${id}`, payload);
}

export async function deleteWalk(id: number): Promise<{ success: boolean; message: string }> {
    return api.delete(`${WALKS_BASE}/${id}`);
}

export async function completeWalk(id: number): Promise<{ success: boolean; walk: SafetyWalk }> {
    return api.post(`${WALKS_BASE}/${id}/complete`, {});
}

export async function getWalkStats(): Promise<{ success: boolean; stats: WalkStats }> {
    return api.get(`${WALKS_BASE}/stats`);
}

/**
 * Streams the walk report PDF to the browser as a file download.
 * Mirrors the lists downloadExport pattern (raw fetch → blob → anchor click).
 */
export async function downloadWalkPdf(id: number): Promise<void> {
    const token = localStorage.getItem('token');
    const base = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/$/, '');
    const response = await fetch(`${base}${WALKS_BASE}/${id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error((data && data.message) || 'Failed to download PDF');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `safety-walk-${id}-report.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/*  Observations                                                       */
/* ------------------------------------------------------------------ */

export async function listObservations(params: ObservationListParams = {}): Promise<ObservationListResponse> {
    const qs = new URLSearchParams();
    if (params.walkId) qs.append('walkId', String(params.walkId));
    if (params.status && params.status !== 'All') qs.append('status', params.status);
    if (params.riskRating) qs.append('riskRating', params.riskRating);
    if (params.overdue) qs.append('overdue', 'true');
    if (params.search) qs.append('search', params.search);
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortDir) qs.append('sortDir', params.sortDir);
    if (params.page) qs.append('page', String(params.page));
    if (params.limit) qs.append('limit', String(params.limit));
    return api.get<ObservationListResponse>(`${OBS_BASE}${qs.toString() ? `?${qs}` : ''}`);
}

export async function listWalkObservations(walkId: number, params: Omit<ObservationListParams, 'walkId'> = {}): Promise<ObservationListResponse> {
    return listObservations({ ...params, walkId });
}

export async function getObservation(id: number): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.get(`${OBS_BASE}/${id}`);
}

export async function createObservation(payload: Partial<SafetyObservation> & { walkId: number }): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.post(`${OBS_BASE}`, payload);
}

export async function addObservationToWalk(walkId: number, payload: Partial<SafetyObservation>): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.post(`${WALKS_BASE}/${walkId}/observations`, payload);
}

export async function updateObservation(id: number, payload: Partial<SafetyObservation>): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.patch(`${OBS_BASE}/${id}`, payload);
}

export async function deleteObservation(id: number): Promise<{ success: boolean; message: string }> {
    return api.delete(`${OBS_BASE}/${id}`);
}

export async function addComment(id: number, text: string): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.post(`${OBS_BASE}/${id}/comments`, { text });
}

export async function submitCloseOut(id: number, payload: { correctiveAction?: string; resolutionNotes?: string; fixedDate?: string; afterPhoto?: unknown }): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.post(`${OBS_BASE}/${id}/close-out`, payload);
}

export async function verifyObservation(id: number, verificationComments?: string): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.post(`${OBS_BASE}/${id}/verify`, { verificationComments });
}

export async function returnObservation(id: number, reason?: string): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.post(`${OBS_BASE}/${id}/return`, { reason });
}

export async function forceCloseObservation(id: number): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.post(`${OBS_BASE}/${id}/force-close`, {});
}

export async function getMyTasks(params: Omit<ObservationListParams, 'walkId'> = {}): Promise<MyTasksResponse> {
    const qs = new URLSearchParams();
    if (params.status && params.status !== 'All') qs.append('status', params.status);
    if (params.search) qs.append('search', params.search);
    if (params.sortBy) qs.append('sortBy', params.sortBy);
    if (params.sortDir) qs.append('sortDir', params.sortDir);
    if (params.page) qs.append('page', String(params.page));
    if (params.limit) qs.append('limit', String(params.limit));
    return api.get<MyTasksResponse>(`${OBS_BASE}/my-tasks${qs.toString() ? `?${qs}` : ''}`);
}

export async function uploadPhoto(id: number, slot: 'beforePhoto' | 'afterPhoto', file: File): Promise<{ success: boolean; observation: SafetyObservation }> {
    const form = new FormData();
    form.append('file', file);
    return api.post(`${OBS_BASE}/${id}/photo?slot=${slot}`, form);
}

export async function removePhoto(id: number, slot: 'beforePhoto' | 'afterPhoto', photoId?: string): Promise<{ success: boolean; observation: SafetyObservation }> {
    return api.delete(`${OBS_BASE}/${id}/photo/${photoId || ''}?slot=${slot}`);
}

/* ------------------------------------------------------------------ */
/*  Settings                                                           */
/* ------------------------------------------------------------------ */

export async function getWalkSettings(): Promise<{ success: boolean; settings: SafetyWalkSettings }> {
    return api.get(`${WALKS_BASE}/settings`);
}

export async function updateWalkSettings(payload: Partial<SafetyWalkSettings>): Promise<{ success: boolean; settings: SafetyWalkSettings }> {
    return api.put(`${WALKS_BASE}/settings`, payload);
}
